// ─────────────────────────────────────────────────────────────────────────────
// Tenant Configuration
// Cada tenant se identifica por el hostname del dominio en producción.
// Para agregar un nuevo tenant: 1) sumar un TenantId, 2) completar su config,
// 3) agregar la entrada en TENANT_MAP.
// ─────────────────────────────────────────────────────────────────────────────

import { PaywayId, ShippingTypeId } from "../actions/enums";
import { PUBLIC_URL } from "../serviceWorker";

export enum TenantId {
  NEWTON = "newton",
  LINK_IMPORTACIONES = "link",
}

/** Tipo de agrupación de variantes de producto. Extensible con string libre. */
export type VariantGroupType = "size" | "color" | string;

export interface RestrictedZipCode {
  city: string;
  zipCode: string;
}

export interface AllowedCellPhone {
  name: string;
  cellPhone: string;
}

export interface TenantFeatures {
  hasResellerLogic: boolean;
  hasMyOrders: boolean;
  hasProductSwap: boolean;
}

export interface ShippingOption {
  value: ShippingTypeId;
  label: string;
}

export interface PaywayOption {
  value: PaywayId;
  label: string;
}

export interface TenantConfig {
  id: TenantId;

  /** ID del shop en el backend (ex user_id). Se usa también para armar URLs de imágenes. */
  shopId: number;

  /** Valor de "post" que se envía al checkout. */
  post: string;

  /** Nombre visible de la marca (footer, title, etc.). */
  brandName: string;

  /** Color principal (navbar, botones de acento). */
  primaryColor: string;

  /** Color de texto sobre primaryColor. */
  primaryTextColor: string;

  /** Color de acento secundario (badges, sale tags). */
  accentColor: string;

  /** Dirección al logo/header del navbar. Se construye con getAssetUrl(). */
  logoFile: string;

  /** Imagen de portada para mobile. */
  homeImageMobileFile: string;

  /** Imagen de portada para desktop. */
  homeImageWideFile: string;

  /** Ancho de la imagen de portada para mobile. */
  homeImageWidth?: string;

  /** Imagen de hot-sale para mobile (opcional). */
  hotSaleImageMobileFile?: string;

  /** Imagen de hot-sale para desktop (opcional). */
  hotSaleImageWideFile?: string;

  /** Imagen de Cyber Monday para mobile (opcional). */
  cyberMondayImageMobileFile?: string;

  /** Imagen de Cyber Monday para desktop (opcional). */
  cyberMondayImageWideFile?: string;

  /** GIF/imagen del badge "premium" (opcional). */
  premiumBadgeFile?: string;

  /** Nombre del archivo del favicon (se sirve desde el CDN de assets). */
  faviconFile: string;

  /** Imagen para Open Graph (WhatsApp, Facebook, etc). Si no se define, se usa faviconFile como fallback. */
  ogImageFile?: string;

  /** Título de la pestaña del navegador. */
  pageTitle: string;

  /** Meta description para SEO. */
  metaDescription: string;

  /** Nombre de la sucursal que se muestra al elegir retiro en local. */
  branchName: string;

  /** ID del tipo de producto "Hot Sale" (para el link del banner). */
  hotSaleTypeId?: number;

  /** ID del tipo de producto "Cyber Monday" (para el link del banner). */
  cyberMondayTypeId?: number;

  /** Ciudades/códigos postales donde no se puede comprar (exclusivo por tenant). */
  restrictedZipCodes: RestrictedZipCode[];

  /** Teléfonos que tienen permiso de saltear la restricción de ciudad. */
  allowedCellPhones: AllowedCellPhone[];

  /** Opciones de envío disponibles para el checkout. Los valores coinciden con la API. */
  shippingOptions: ShippingOption[];

  /** Opciones de pago disponibles para el checkout. Los valores coinciden con la API. */
  paywayOptions: PaywayOption[];

  /** Feature flags para activar/desactivar lógicas por tenant. */
  features: TenantFeatures;

  // ─── Contacto y Redes ───────────────────────────────────────────────────────
  contactAddress?: string;
  contactPhone?: string;
  contactEmail?: string;
  socialWhatsapp?: string;
  socialInstagram?: string;
  socialFacebook?: string;
  mapIframeSrc?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Construye la URL de un asset de imagen dado el shopId y el nombre de archivo.
 * Ejemplo: getAssetUrl(1, "header.png") → "img/user/1/header.png"
 * Se usa junto con PUBLIC_URL del serviceWorker.
 */
export const buildAssetPath = (shopId: number, file: string): string =>
  `img/user/${shopId}/${file}`;

/**
 * Construye la URL completa de un asset de imagen (listo para usar en un src).
 * Devuelve "" si no hay archivo configurado, para que el componente no renderice la imagen.
 * Ejemplo: buildAssetUrl(1, "header.png") → "<PUBLIC_URL>img/user/1/header.png"
 */
export const buildAssetUrl = (shopId: number, file?: string): string =>
  file ? PUBLIC_URL + buildAssetPath(shopId, file) : "";

// ─────────────────────────────────────────────────────────────────────────────
// Configuraciones por tenant
// ─────────────────────────────────────────────────────────────────────────────

const NEWTON_CONFIG: TenantConfig = {
  id: TenantId.NEWTON,
  shopId: 1,
  post: "1",
  brandName: "Newton",
  primaryColor: "#d2fb1a",
  primaryTextColor: "#000000",
  accentColor: "#d2fb1a",
  logoFile: "header.png",
  homeImageMobileFile: "home-mobile.png",
  homeImageWideFile: "home-wide.png",
  hotSaleImageMobileFile: "hot-sale.jpg",
  hotSaleImageWideFile: "hot-sale-wide.jpg",
  cyberMondayImageMobileFile: "cyber-monday-mobile.jpeg",
  cyberMondayImageWideFile: "cyber-monday.jpeg",
  premiumBadgeFile: "premium.gif",
  faviconFile: "favicon.png",
  pageTitle: "Newton Store | Indumentaria deportiva online",
  metaDescription:
    "Comprar ropa y accesorios deportivos por internet en Newton Store, accedé a lo mejor de la moda. Cambio y devolución gratis a toda Argentina.",
  branchName: "Sucursal Newton",
  hotSaleTypeId: 322,
  cyberMondayTypeId: 547,
  restrictedZipCodes: [{ city: "Piamonte", zipCode: "2529" }],
  features: { hasResellerLogic: true, hasMyOrders: true, hasProductSwap: true },
  allowedCellPhones: [{ name: "Carolina Ñañez", cellPhone: "3401646138" }],
  shippingOptions: [
    {
      value: ShippingTypeId.CADETE,
      label: "Cadete (Vivís en Rosario y alrededores)",
    },
    {
      value: ShippingTypeId.BRANCH_PICKUP,
      label: "Retirás en el local (España 1025 - Rosario)",
    },
    {
      value: ShippingTypeId.CORREO_ARGENTINO,
      label: "Correo argentino (Retirás en correo más cercano)",
    },
  ],
  paywayOptions: [
    {
      value: PaywayId.CASH,
      label: "Contado (Sólo si retirás en el local o recibís con cadete)",
    },
    {
      value: PaywayId.TRANSFER,
      label: "Depósito / Transferencia bancaria (Enviamos cuenta a tu email)",
    },
    {
      value: PaywayId.CARD,
      label: "Tarjetas Crédito Débito - Rapipago - PagoFacil",
    },
  ],

  // Contacto Newton
  contactAddress: "España 1025, Rosario, Santa Fe",
  contactPhone: "3412470782",
  contactEmail: "newtonclothes18@gmail.com",
  socialWhatsapp: "https://api.whatsapp.com/send?phone=5493412470782",
  socialInstagram: "https://www.instagram.com/newton_clothes/",
  socialFacebook: "https://www.facebook.com/newtons84/",
  mapIframeSrc:
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3348.1178007193075!2d-60.64725791182893!3d-32.94789912872239!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x95b7ab1434d553f7%3A0xdae75cb1e8a97b6e!2sEspa%C3%B1a%201025%2C%20S2000%20Rosario%2C%20Santa%20Fe!5e0!3m2!1ses!2sar!4v1600462900292!5m2!1ses!2sar",
};

const LINK_CONFIG: TenantConfig = {
  id: TenantId.LINK_IMPORTACIONES,
  shopId: 2,
  post: "1",
  brandName: "Link Importaciones",
  primaryColor: "#ffcae6",
  primaryTextColor: "#333333",
  accentColor: "#ffcae6",
  logoFile: "logo-web.png",
  homeImageMobileFile: "",
  homeImageWideFile: "",
  homeImageWidth: "",
  hotSaleImageMobileFile: "",
  hotSaleImageWideFile: "",
  cyberMondayImageMobileFile: "",
  cyberMondayImageWideFile: "",
  premiumBadgeFile: "",
  faviconFile: "favicon-color.png",
  pageTitle: "Link Importaciones",
  metaDescription:
    "Descubrí nuestros productos importados de diseño exclusivo, de alta demanda y calidad premium.",
  branchName: "Sucursal Link Importaciones",
  hotSaleTypeId: undefined,
  cyberMondayTypeId: undefined,
  restrictedZipCodes: [],
  features: {
    hasResellerLogic: false,
    hasMyOrders: true,
    hasProductSwap: false,
  },
  allowedCellPhones: [],
  shippingOptions: [
    {
      value: ShippingTypeId.BRANCH_PICKUP,
      label: "Retiro (Mendoza 1773 - Rosario)",
    },
    {
      value: ShippingTypeId.CORREO_ARGENTINO,
      label: "Correo argentino (sucursal o domicilio)",
    },
  ],
  paywayOptions: [
    { value: PaywayId.CASH, label: "Efectivo (Sólo si retirás)" },
    { value: PaywayId.TRANSFER, label: "Transferencia bancaria (Para envíos)" },
  ],

  // Contacto Link Importaciones
  contactAddress: "Mendoza 1773 Rosario Centro",
  contactPhone: "3417806036",
  contactEmail: "link.importacioness@gmail.com",
  socialWhatsapp: "https://wa.me/3417806036",
  socialInstagram:
    "https://www.instagram.com/linkimportaciones?igsh=MnpudzMwYTFvbjFi&utm_source=qr",
  socialFacebook: undefined,
  mapIframeSrc:
    "https://maps.google.com/maps?q=Mendoza%201773%20Rosario&t=&z=15&ie=UTF8&iwloc=&output=embed",
};

// ─────────────────────────────────────────────────────────────────────────────
// Mapa hostname → TenantConfig
// Agregar aquí los dominios de producción de cada tenant.
// ─────────────────────────────────────────────────────────────────────────────

// Para probar distintos tenants en entorno local (localhost), cambia esta variable:
const LOCAL_DEV_TENANT = NEWTON_CONFIG; // Opciones: NEWTON_CONFIG, LINK_CONFIG

const TENANT_MAP: Record<string, TenantConfig> = {
  // Newton
  "newton.com.ar": NEWTON_CONFIG,
  "www.newton.com.ar": NEWTON_CONFIG,
  "newton.estudionube.com.ar": NEWTON_CONFIG,

  // Link Importaciones
  "linkimportaciones.com.ar": LINK_CONFIG,
  "www.linkimportaciones.com.ar": LINK_CONFIG,
  "link.estudionube.com.ar": LINK_CONFIG,

  // Fallback local (desarrollo)
  localhost: LOCAL_DEV_TENANT,
};

/**
 * Resuelve el TenantConfig según el hostname actual.
 * Si el hostname no está mapeado, devuelve el config de Newton como fallback.
 */
export const getTenantConfig = (): TenantConfig =>
  TENANT_MAP[window.location.hostname] ?? NEWTON_CONFIG;
